import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  CloseOutlined as CloseIcon,
  ContentCopyOutlined as CopyIcon,
  PhoneOutlined as PhoneIcon,
} from '@mui/icons-material';

import HiringService from '../../api/services/hiring.service';

/**
 * The call, on the screen you are taking notes on.
 *
 * The script lived in a Google Doc in another tab — one copy per company,
 * because the pay differs, which is how somebody at one company gets
 * quoted the other one's rate. It is one script now, with this company's
 * numbers and this candidate's name already in it.
 *
 * A dialog, the same size as the message composer. It WAS full screen —
 * an interview is ten minutes of listening while writing, so the script
 * and the box are the only two things that matter — but at that size it
 * owns the whole window and there is nowhere to put anything else. The
 * script scrolls inside a fixed height instead, which leaves the page it
 * came from visible around it and room to grow.
 *
 * The script is plain text with three characters of convention, parsed
 * here: `#` a section, `?` a question, `-` a point to make.
 *
 * A question carries a TICK, not an answer. It briefly had yes/no buttons
 * that wrote a summary into the notes, and that was two mistakes: it made
 * a structured record out of a conversation, and it put words in the
 * notes that nobody typed. The tick means "covered this" — a place to
 * come back to if the call is interrupted — and it saves itself. What was
 * actually said is the notes, in somebody's own words.
 */

type Line =
  | { kind: 'heading'; text: string }
  | { kind: 'ask'; text: string; id: string }
  | { kind: 'point'; text: string }
  | { kind: 'text'; text: string };

/** The script, as lines the screen can render. */
export function parseGuide(body: string): Line[] {
  return body.split('\n').map((raw, index) => {
    const line = raw.trimEnd();
    if (line.startsWith('# ')) return { kind: 'heading', text: line.slice(2) } as Line;
    if (line.startsWith('? ')) return { kind: 'ask', text: line.slice(2), id: `q${index}` } as Line;
    if (line.startsWith('- ')) return { kind: 'point', text: line.slice(2) } as Line;
    return { kind: 'text', text: line } as Line;
  });
}

const InterviewMode: React.FC<{
  open: boolean;
  candidateId: string;
  candidateName: string;
  phone?: string | null;
  notes: string;
  /** Questions already covered, by their own text. */
  covered?: string[];
  onClose: () => void;
  onSaveNotes: (notes: string) => Promise<void> | void;
  onSaveCovered: (covered: string[]) => Promise<void> | void;
}> = ({
  open, candidateId, candidateName, phone, notes, covered = [],
  onClose, onSaveNotes, onSaveCovered,
}) => {
  const [body, setBody] = useState('');
  const [gaps, setGaps] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState(notes || '');
  const [saved, setSaved] = useState(true);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraft(notes || '');
    setSaved(true);
    setError(null);
    HiringService.guide(candidateId)
      .then((result) => { setBody(result.body); setGaps(result.gaps || []); })
      .catch(() => setError('The interview guide could not be loaded.'));
    // `notes` is deliberately NOT a dependency.
    //
    // Saving notes updates the record, the record comes back down as this
    // prop, and re-seeding from it put the server's copy into the box
    // mid-sentence — swallowing whatever had been typed since the save
    // started, which showed up as characters and spaces disappearing
    // under the cursor. The box is seeded when the screen OPENS and is
    // the authority until it closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidateId]);

  const lines = useMemo(() => parseGuide(body), [body]);

  /**
   * Notes save themselves a beat after typing stops. Somebody on a call
   * is not going to press Save, and a call that ends abruptly is exactly
   * when the last thing said matters most.
   */
  const queueSave = useCallback((text: string) => {
    setSaved(false);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // Long enough to be a pause rather than a gap between words. At 800ms
    // a save fired between sentences, and every save was a chance for the
    // round trip to land on top of the next one.
    saveTimer.current = setTimeout(async () => {
      await onSaveNotes(text);
      setSaved(true);
    }, 1500);
  }, [onSaveNotes]);

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const finish = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // Only what was typed. Nothing this screen invents ends up in the
    // notes — a record of a conversation should be in the words of the
    // person who had it.
    if (draft !== notes) await onSaveNotes(draft);
    onClose();
  };

  /**
   * Ticking saves immediately: a call can end at any moment.
   *
   * There is NO local copy of the ticks. There was, and it is why they
   * came back empty: the screen showed its own list, the record held
   * another, and reopening seeded from whichever had arrived first. The
   * record is the only list; the parent applies the change at once and
   * then persists it.
   */
  const toggle = (text: string) => {
    const next = covered.includes(text)
      ? covered.filter((t) => t !== text)
      : [...covered, text];
    Promise.resolve(onSaveCovered(next)).catch(() => { /* the parent says so */ });
  };

  return (
    <Dialog open={open} onClose={finish} maxWidth="md" fullWidth>
      <Stack
        direction="row" alignItems="center" gap={2}
        sx={{ px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Typography variant="h6" sx={{ fontSize: 17 }}>{candidateName}</Typography>
        {phone && (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <PhoneIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">{phone}</Typography>
            <Tooltip title="Copy the number">
              <IconButton
                size="small"
                aria-label="Copy the number"
                onClick={() => navigator.clipboard?.writeText(phone)}
                sx={{ color: 'text.disabled' }}
              >
                <CopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.disabled">
          {saved ? 'Notes saved' : 'Saving…'}
        </Typography>
        <Button startIcon={<CloseIcon />} onClick={finish}>Done</Button>
      </Stack>

      {/* One height, whatever the script's length: a dialog that grows
          with its content would be a different size for every company. */}
      <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: '62vh', minHeight: 0 }}>
        {/* The script. Scrolls on its own, set larger than the rest of the
            product — this is read at arm's length while talking. */}
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', px: 3, py: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {gaps.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {gaps.join(' and ')} {gaps.length > 1 ? 'are' : 'is'} not set up yet — fill
              the pay in under Settings → Hiring so the script can quote it.
            </Alert>
          )}

          <Box>
            {lines.map((line, index) => {
              if (line.kind === 'heading') {
                return (
                  <Typography
                    key={index}
                    variant="overline"
                    sx={{
                      display: 'block', mt: 3, mb: 1.25, color: 'text.disabled',
                      fontWeight: 700, letterSpacing: '.08em',
                    }}
                  >
                    {line.text}
                  </Typography>
                );
              }

              if (line.kind === 'ask') {
                const done = covered.includes(line.text);
                return (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="flex-start"
                    gap={1}
                    onClick={() => toggle(line.text)}
                    sx={{
                      py: 0.75, px: 1, mb: 0.5, borderRadius: 1.5, cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Checkbox
                      checked={done}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); toggle(line.text); }}
                      inputProps={{ 'aria-label': line.text }}
                      // The box is 24px tall, so the first line of the
                      // question is set to 24px too and the two centre on
                      // each other. It had a 2px nudge downward against a
                      // 22.5px line, which is why every question sat above
                      // its own checkbox.
                      sx={{ p: 0.25, mt: 0, alignSelf: 'flex-start' }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        flex: 1, minWidth: 0, fontSize: 15, lineHeight: '24px',
                        color: done ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {line.text}
                    </Typography>
                  </Stack>
                );
              }

              if (line.kind === 'point') {
                return (
                  <Stack key={index} direction="row" gap={1.25} sx={{ mb: 0.75, pl: 1.5 }}>
                    <Box sx={{
                      width: 5, height: 5, borderRadius: '50%', bgcolor: 'text.disabled',
                      mt: '10px', flexShrink: 0,
                    }} />
                    <Typography variant="body1" sx={{ fontSize: 15, lineHeight: '24px' }}>
                      {line.text}
                    </Typography>
                  </Stack>
                );
              }

              if (!line.text) return <Box key={index} sx={{ height: 12 }} />;
              return (
                <Typography key={index} variant="body1" sx={{ mb: 1, fontSize: 15, lineHeight: 1.6 }}>
                  {line.text}
                </Typography>
              );
            })}
          </Box>
        </Box>

        {/* Notes, the width of a column you can actually write in. */}
        <Box
          sx={{
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            borderLeft: { md: '1px solid' },
            borderTop: { xs: '1px solid', md: 'none' },
            borderColor: 'divider',
            display: 'flex', flexDirection: 'column', minHeight: 0,
            p: 2.5, bgcolor: 'action.hover',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Notes</Typography>
          <TextField
            multiline
            fullWidth
            autoFocus
            placeholder="What they said…"
            value={draft}
            onChange={(e) => { setDraft(e.target.value); queueSave(e.target.value); }}
            inputProps={{ 'aria-label': 'Interview notes' }}
            sx={{
              flex: 1,
              bgcolor: 'background.paper',
              '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
              '& .MuiInputBase-input': { fontSize: 14, lineHeight: 1.6 },
            }}
          />
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
            Saved as you type.
          </Typography>
        </Box>
      </Stack>
    </Dialog>
  );
};

export default InterviewMode;
